import {ProfileSalesAssistanceTabContent} from "./tabs/profileSalesAssistanceTabContent";
import type {ReactNode} from "react";
import type {EventsTabProfileListOverrides} from "~/components/entityProfile/eventsTabProfileListOverrides";
import type {
    EntityProfileKind,
    ProfileTabId,
    ProfileTabRenderProps,
} from "~/components/entityProfile/types";
import type {CustomerProfileLifecycleContext} from "~/components/entityProfile/lifecycle/types";
import {ProfileActivityTabContent} from "~/components/entityProfile/tabs/profileActivityTabContent";
import {ProfileCustomerActivityTabContent} from "~/components/entityProfile/tabs/profileCustomerActivityTabContent";
import {ProfileCustomerEventsTabContent} from "~/components/entityProfile/tabs/profileCustomerEventsTabContent";
import {ProfileCustomerNotesTabContent} from "~/components/entityProfile/tabs/profileCustomerNotesTabContent";
import {ProfileDetailsTabContent} from "~/components/entityProfile/tabs/profileDetailsTabContent";
import {ProfileEventsTabContent} from "~/components/entityProfile/tabs/profileEventsTabContent";
import {ProfileNotesTabContent, type JourneyNotesTabContext} from "~/components/entityProfile/tabs/profileNotesTabContent";
import type {CustomerProfileActivityContext, CustomerProfileEventsContext, CustomerProfileJourneysContext, CustomerProfileNotesContext} from "~/components/entityProfile/customerProfileDataSource/types";
import type {CustomerProfileOverviewContext} from "~/components/entityProfile/overview/types";
import type {EntityProfileSummaryField} from "~/components/entityProfile/types";
import type {OttopilotCustomer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotCustomersWorkspaceCapability/domain/OttopilotCustomer";
import {ProfileJourneysTabContent} from "~/components/entityProfile/tabs/profileJourneysTabContent";
import {ProfileLifecycleTabContent} from "~/components/entityProfile/tabs/profileLifecycleTabContent";
import {ProfileOverviewTabContent} from "~/components/entityProfile/tabs/profileOverviewTabContent";
import type {EventContainerWithEvents} from "~/utilities/apis/api";

export type JourneyProfileTabContext = {
    journey: EventContainerWithEvents;
    notes: JourneyNotesTabContext;
    detailsFields: Array<EntityProfileSummaryField>;
    onCopyContact?: (value: string) => void;
    /** Closes footer-driven journey panels (edit / create / more) before event row actions open. */
    dismissFooterEngagementPanels: () => void;
    /** Events tab registers how to close an open event row panel before footer actions run. */
    registerDismissEventRowEngagement: (dismiss: () => void) => void;
    /** Harness JourneyProfile: config-driven Events tab chrome. Production leaves undefined. */
    eventsListOverrides?: EventsTabProfileListOverrides;
};

export type CustomerProfileTabContext = {
    customer: OttopilotCustomer;
    journeyDisplayName: string;
    detailsFields: Array<EntityProfileSummaryField>;
    onCopyContact?: (value: string) => void;
    overview: CustomerProfileOverviewContext;
    lifecycle: CustomerProfileLifecycleContext;
    activity: CustomerProfileActivityContext;
    notes: CustomerProfileNotesContext;
    events: CustomerProfileEventsContext;
    journeys: CustomerProfileJourneysContext;
    dismissFooterEngagementPanels: () => void;
    registerDismissEventRowEngagement: (dismiss: () => void) => void;
};

export type ProfileTabContextByKind = {
    journey: JourneyProfileTabContext;
    customer: CustomerProfileTabContext;
};

type ProfileTabModule = {
    id: ProfileTabId;
    supportedKinds: Array<EntityProfileKind>;
    render: (args: {
        kind: EntityProfileKind;
        tabRenderProps: ProfileTabRenderProps;
        context: ProfileTabContextByKind[EntityProfileKind];
    }) => ReactNode;
};

const profileTabRegistry: Partial<Record<ProfileTabId, ProfileTabModule>> = {
    "sales-assistance": {
        id: "sales-assistance", supportedKinds: ["journey"],
        render: ({context}) => <ProfileSalesAssistanceTabContent journey={(context as JourneyProfileTabContext).journey} />,
    },
    events: {
        id: "events",
        supportedKinds: ["journey", "customer"],
        render: ({kind, tabRenderProps, context}) => {
            if (kind === "customer") {
                const customerContext = context as CustomerProfileTabContext;
                return (
                    <ProfileCustomerEventsTabContent
                        tabRenderProps={tabRenderProps}
                        context={customerContext}
                    />
                );
            }

            const journeyContext = context as JourneyProfileTabContext;
            return (
                <ProfileEventsTabContent
                    tabRenderProps={tabRenderProps}
                    journey={journeyContext.journey}
                    dismissFooterEngagementPanels={journeyContext.dismissFooterEngagementPanels}
                    registerDismissEventRowEngagement={journeyContext.registerDismissEventRowEngagement}
                    profileListOverrides={journeyContext.eventsListOverrides}
                />
            );
        },
    },
    notes: {
        id: "notes",
        supportedKinds: ["journey", "customer"],
        render: ({kind, tabRenderProps, context}) => {
            if (kind !== "journey") {
                const customerContext = context as CustomerProfileTabContext;
                return (
                    <ProfileCustomerNotesTabContent
                        tabRenderProps={tabRenderProps}
                        context={customerContext}
                    />
                );
            }

            return (
                <ProfileNotesTabContent
                    tabRenderProps={tabRenderProps}
                    context={(context as JourneyProfileTabContext).notes}
                />
            );
        },
    },
    activity: {
        id: "activity",
        supportedKinds: ["journey", "customer"],
        render: ({kind, tabRenderProps, context}) => {
            if (kind === "customer") {
                const customerContext = context as CustomerProfileTabContext;
                return (
                    <ProfileCustomerActivityTabContent
                        tabRenderProps={tabRenderProps}
                        activity={customerContext.activity}
                    />
                );
            }

            return (
                <ProfileActivityTabContent
                    tabRenderProps={tabRenderProps}
                    journey={(context as JourneyProfileTabContext).journey}
                />
            );
        },
    },
    journeys: {
        id: "journeys",
        supportedKinds: ["customer"],
        render: ({tabRenderProps, context}) => (
            <ProfileJourneysTabContent
                tabRenderProps={tabRenderProps}
                context={context as CustomerProfileTabContext}
            />
        ),
    },
    overview: {
        id: "overview",
        supportedKinds: ["customer"],
        render: ({tabRenderProps, context}) => (
            <ProfileOverviewTabContent
                tabRenderProps={tabRenderProps}
                context={context as CustomerProfileTabContext}
            />
        ),
    },
    lifecycle: {
        id: "lifecycle",
        supportedKinds: ["customer"],
        render: ({tabRenderProps, context}) => (
            <ProfileLifecycleTabContent
                tabRenderProps={tabRenderProps}
                context={context as CustomerProfileTabContext}
            />
        ),
    },
    details: {
        id: "details",
        supportedKinds: ["journey", "customer"],
        render: ({kind, context}) => {
            if (kind === "customer") {
                const customerContext = context as CustomerProfileTabContext;
                return (
                    <ProfileDetailsTabContent
                        fields={customerContext.detailsFields}
                        onCopyContact={customerContext.onCopyContact}
                    />
                );
            }

            const journeyContext = context as JourneyProfileTabContext;
            return (
                <ProfileDetailsTabContent
                    fields={journeyContext.detailsFields}
                    onCopyContact={journeyContext.onCopyContact}
                />
            );
        },
    },
};

export function isProfileTabSupportedForKind(tabId: ProfileTabId, kind: EntityProfileKind): boolean {
    const module = profileTabRegistry[tabId];
    return module !== undefined && module.supportedKinds.includes(kind);
}

export function renderProfileTabContent(args: {
    tabId: ProfileTabId;
    kind: EntityProfileKind;
    tabRenderProps: ProfileTabRenderProps;
    context: ProfileTabContextByKind[EntityProfileKind];
}): ReactNode {
    const module = profileTabRegistry[args.tabId];
    if (module === undefined || module.supportedKinds.includes(args.kind) !== true) {
        return null;
    }

    return module.render({
        kind: args.kind,
        tabRenderProps: args.tabRenderProps,
        context: args.context,
    });
}

export function getDefaultJourneyProfileTabs() {
    return [
        {id: "events" as const, label: "Events"},
        {id: "activity" as const, label: "Activity"},
        {id: "notes" as const, label: "Notes", requiresLeaveGuard: true},
        {id: "details" as const, label: "Details"},
    ];
}

export function getDefaultCustomerProfileTabs() {
    return [
        {id: "overview" as const, label: "Overview"},
        {id: "lifecycle" as const, label: "Lifecycle"},
        {id: "events" as const, label: "Events"},
        {id: "notes" as const, label: "Notes", requiresLeaveGuard: true},
        {id: "activity" as const, label: "Activity"},
        {id: "details" as const, label: "Details"},
    ];
}
